import type { GetLinkableRequestsResponse } from "@nlc/shared-types";

import { RequestStatusEnum } from "@nlc/shared-types";
import dayjs from "dayjs";

export const MOCKED_LINKABLE_REQUESTS: GetLinkableRequestsResponse = {
  requests: [
    { id: 101, name: "Landing page redesign", requestType: "website_design" },
    { id: 102, name: "Email newsletter header", requestType: "graphic" },
    { id: 103, name: "Promo teaser video", requestType: "video" },
    { id: 104, name: "Brand guidelines refresh", requestType: "graphic" },
  ],
};

export const MOCKED_REQUEST = {
  graphic: {
    assignee: {
      designation: "Graphic Assistant Team Leader",
      id: 0,
      name: "Demo user",
    },
    brandId: 0,
    briefText: "",
    collaboratorsIds: [],
    complexityId: null,
    createdAt: new Date("2025-09-26T16:44:09.000Z"),
    creativeDirection: "Designer has creative freedom" as const,
    creator: { id: 1, name: "Demo customer", profilePicture: "" },
    description:
      "<p>We need a set of 3 social media ads to promote our new Fall Collection launch. Deliverables should be provided in the following sizes:</p><ul>  <li>Instagram Feed (1080×1080)</li>  <li>Instagram Story (1080×1920)</li></ul><p><strong>Total: 3 ad concepts × 2 sizes each = 6 deliverables.</strong></p>",
    designerId: 0,
    designTypeId: 12,
    dueDate: dayjs().add(2, "day").toDate(),
    id: 1,
    internalDueDate: null,
    internalNotes: null,
    isAiStudio: false,
    isDraft: false,
    isMbc: false,
    lengthOrPlatform: null,
    linkedRequestIds: [101, 102],
    magicalBriefCreator: {
      additionalGuidelines: "",
      brandGuidelines: "",
      creativeGoals: "",
      keyMessageAndCta: "",
      scope: "",
      script: "",
      textForDesign: "",
    },
    musicGenre: null,
    name: "Fall Collection ads",
    preferredDesignerIds: [],
    preferredFileType: ".PNG",
    projectId: 0,
    requestType: "graphic",
    restrictAiAssets: false,
    sizesNeeded: "1080x1080",
    statusId: RequestStatusEnum.ReadyForReview,
    styleOfVideo: null,
    textForDesign: `<b>Ad 1 – Lifestyle Highlight</b>
<ul>
  <li><strong>Image:</strong> Use the photo of the model wearing the brown leather jacket ("FallJacket_01.jpg").</li>
  <li><strong>Style:</strong> Warm tones, cozy fall vibe, light overlay texture.</li>
  <li><strong>Text:</strong>
    <ul>
      <li>Header: "Fall Into Style"</li>
      <li>Subtext: "Discover our new Fall Collection."</li>
      <li>CTA: "Shop Now"</li>
    </ul>
  </li>
</ul>

<b>Ad 2 – Product Focus</b>

<ul>
  <li><strong>Image:</strong> Use the product flat lay ("FallBoots_Flatlay.png").</li>
  <li><strong>Style:</strong> Clean, minimal background, emphasize product details.</li>
  <li><strong>Text:</strong>
    <ul>
      <li>Header: "Your Perfect Fall Boots"</li>
      <li>Subtext: "Crafted for comfort and style."</li>
      <li>CTA: "See the Collection"</li>
    </ul>
  </li>
</ul>

<b>Ad 3 – Promo / Offer</b>

<ul>
  <li><strong>Image:</strong> Use the group shot of models outdoors ("FallGroup_Outdoor.jpg").</li>
  <li><strong>Style:</strong> Vibrant, energetic, capture the excitement of the season.</li>
  <li><strong>Text:</strong>
    <ul>
      <li>Header: "Fall Collection Launch!"</li>
      <li>Subtext: "20% OFF this week only."</li>
      <li>CTA: "Shop the Sale"</li>
    </ul>
  </li>
</ul>`,
  },
  video: {
    assignee: {
      designation: "Graphic Assistant Team Leader",
      id: 0,
      name: "Demo user",
    },
    brandId: 0,
    briefText: "",
    collaboratorsIds: [],
    complexityId: null,
    createdAt: new Date("2025-09-26T16:44:09.000Z"),
    creativeDirection: "Designer has creative freedom" as const,
    creator: { id: 1, name: "Demo customer", profilePicture: "" },
    description:
      "<p>We need 2 video ads to promote our new fitness app. Deliverables:</p><ul><li>Ad 1: 15-second video (1080×1920 – TikTok, Instagram Reels, Stories)</li><li>Ad 2: 20 to 30-second video (1080×1920 – TikTok, Instagram Reels, Stories)</li></ul><p><strong>Total: 2 ads in 1 size = 2 deliverables.</strong></p>",
    designerId: 0,
    designTypeId: 1,
    dueDate: dayjs().add(1, "day").toDate(),
    id: 1,
    internalDueDate: null,
    internalNotes: null,
    isAiStudio: false,
    isDraft: false,
    isMbc: false,
    lengthOrPlatform: null,
    linkedRequestIds: [101, 102],
    magicalBriefCreator: {
      additionalGuidelines: "",
      brandGuidelines: "",
      creativeGoals: "",
      keyMessageAndCta: "",
      scope: "",
      script: "",
      textForDesign: "",
    },
    musicGenre: null,
    name: "Fitness app ads",
    preferredDesignerIds: [],
    preferredFileType: ".mp4",
    projectId: 0,
    requestType: "video",
    restrictAiAssets: false,
    sizesNeeded: "1080x1920",
    statusId: RequestStatusEnum.ReadyForReview,
    styleOfVideo: null,
    textForDesign: `<p><strong>Ad 1 – Quick Lifestyle Promo (15s)</strong></p>

<p>0:00–0:03 → Hook: text overlay "Your Fitness, Your Way " with upbeat music.</p>

<p>0:03–0:07 → Clip: woman running outdoors, overlay "Track your workouts."</p>

<p>0:07–0:12 → App screen recording showing workout tracker.</p>

<p>0:12–0:15 → Logo + CTA: "Download Now."</p>

<p><strong>Ad 2 – App Walkthrough (20 to 30s)</strong></p>

<p>0:00–0:04 → Hook: "Stay motivated. Anytime. Anywhere."</p>

<p>0:04–0:10 → Screen recording: dashboard, progress charts, reminders.</p>

<p>0:10–0:14 → Lifestyle clips: running, lifting, yoga. Overlay text: "Workouts. Meals. Progress."</p>

<p>0:14–0:20 → Voiceover/testimonial: "This app keeps me on track!"</p>

<p>0:20–0:24 → Logo + CTA: "Start your free trial today."</p>`,
  },
};
